/**
 * DemoOuttake — a Card framing a small interactive demo snippet, used mid-essay
 * on About and in the Functionality / Demo pages' card-picker. The interactive
 * body is passed as children (or registered by demoId upstream); this primitive
 * only supplies the titled, bordered frame. Tokens + Tailwind only.
 */
import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../card';
import { FlowDemo, DEMO_SCENARIO_BY_ID } from './FlowDemo';

export interface DemoOuttakeProps {
  /** Demo scenario id this frame hosts — the primitive renders the matching
   *  FlowDemo itself (self-contained, so it's a uniform block in the registry). */
  readonly demoId: string;
  /** Autoplay the hosted FlowDemo (loops, for embeds). */
  readonly autoplay?: boolean;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Optional override body; when absent the FlowDemo for `demoId` is rendered. */
  readonly children?: ReactNode;
}

export function DemoOuttake({ demoId, autoplay, title, description, children }: DemoOuttakeProps) {
  const scenario = DEMO_SCENARIO_BY_ID[demoId];
  const body = children ?? (scenario ? <FlowDemo scenario={scenario} autoplay={autoplay} /> : null);
  return (
    <Card className="overflow-hidden" data-demo={demoId} interactive={false}>
      {(title || description) && (
        <CardHeader bordered={!!body}>
          {/* h2: a demo outtake is a top-level section (it heads the demo card,
              often directly under the page hero with no section heading between).
              Keeps heading order contiguous; CardTitle size is unchanged. */}
          {title && <CardTitle as="h2">{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      {body && <CardContent className="pt-6">{body}</CardContent>}
    </Card>
  );
}
